import { useEffect } from 'react';
import { idlePreloadPaths, preloadPageChunk } from '../config/routeChunks';

// Let the landing page and its data requests go first.
const START_DELAY_MS = 2000;

const whenIdle = () =>
  new Promise((resolve) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(resolve, { timeout: 5000 });
    } else {
      // Safari has no requestIdleCallback.
      setTimeout(resolve, 200);
    }
  });

// Respect the user's data budget and a hidden tab: the preload is a guess
// about the next click, and it is not worth bytes the user asked us to save.
// https://web.dev/articles/optimizing-content-efficiency-save-data
const shouldSkip = () => {
  const connection = navigator.connection;
  return (
    document.visibilityState !== 'visible' ||
    Boolean(connection?.saveData) ||
    /2g$/.test(connection?.effectiveType ?? '')
  );
};

/**
 * Warms the page chunks an org type is most likely to open next, after the
 * workspace has painted.
 *
 * One chunk at a time, each behind its own idle callback, so the work never
 * piles up into a long task and yields to anything the user does. Page chunks
 * are small; the heavy vendor families are deliberately not in the list.
 */
export function useIdlePageChunkPreload(orgType, { enabled = true } = {}) {
  useEffect(() => {
    const paths = idlePreloadPaths[orgType];
    if (!enabled || !paths) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      for (const path of paths) {
        await whenIdle();
        if (cancelled || shouldSkip()) return;
        await preloadPageChunk(path);
      }
    }, START_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orgType, enabled]);
}

export default useIdlePageChunkPreload;
